import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, RotateCcw, Eye, Download, ChevronDown, Palette, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { HexisColorPalette } from "@/components/HexisColorPalette";
import { Editor } from '@tinymce/tinymce-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Size options with pricing (100mm to 3556mm)
const SIZE_OPTIONS = [
  { id: "100mm", name: "100mm", dimensions: "10cm", price: 10.99 },
  { id: "150mm", name: "150mm", dimensions: "15cm", price: 12.99 },
  { id: "200mm", name: "200mm", dimensions: "20cm", price: 14.99 },
  { id: "250mm", name: "250mm", dimensions: "25cm", price: 16.99 },
  { id: "300mm", name: "300mm", dimensions: "30cm", price: 18.99 },
  { id: "350mm", name: "350mm", dimensions: "35cm", price: 20.99 },
  { id: "400mm", name: "400mm", dimensions: "40cm", price: 22.99 },
  { id: "450mm", name: "450mm", dimensions: "45cm", price: 24.99 },
  { id: "500mm", name: "500mm", dimensions: "50cm", price: 26.99 },
  { id: "600mm", name: "600mm", dimensions: "60cm", price: 29.99 },
  { id: "700mm", name: "700mm", dimensions: "70cm", price: 32.99 },
  { id: "800mm", name: "800mm", dimensions: "80cm", price: 35.99 },
  { id: "900mm", name: "900mm", dimensions: "90cm", price: 38.99 },
  { id: "1000mm", name: "1000mm", dimensions: "1m", price: 41.99 },
  { id: "1200mm", name: "1200mm", dimensions: "1.2m", price: 45.99 },
  { id: "1500mm", name: "1500mm", dimensions: "1.5m", price: 49.99 },
  { id: "1800mm", name: "1800mm", dimensions: "1.8m", price: 54.99 },
  { id: "2000mm", name: "2000mm", dimensions: "2m", price: 59.99 },
  { id: "2500mm", name: "2500mm", dimensions: "2.5m", price: 64.99 },
  { id: "3000mm", name: "3000mm", dimensions: "3m", price: 69.99 },
  { id: "3556mm", name: "3556mm", dimensions: "3.56m", price: 79.99 },
];

// Font options - organized by category with more TTF styles (ALPHABETICALLY SORTED)
const FONT_OPTIONS = [
  // Display & Bold (High Impact) - Extra Bold Styles
  { id: "anton", name: "Anton", style: "Anton, sans-serif", category: "display" },
  { id: "arial-black", name: "Arial Black", style: "'Arial Black', sans-serif", category: "display" },
  { id: "archivo-black", name: "Archivo Black", style: "'Archivo Black', sans-serif", category: "display" },
  { id: "bangers", name: "Bangers", style: "Bangers, cursive", category: "display" },
  { id: "bebas-neue", name: "Bebas Neue", style: "'Bebas Neue', sans-serif", category: "display" },
  { id: "big-shoulders", name: "Big Shoulders Display", style: "'Big Shoulders Display', cursive", category: "display" },
  { id: "black-ops-one", name: "Black Ops One", style: "'Black Ops One', cursive", category: "display" },
  { id: "bowlby-one", name: "Bowlby One SC", style: "'Bowlby One SC', cursive", category: "display" },
  { id: "bungee-shade", name: "Bungee Shade", style: "'Bungee Shade', cursive", category: "display" },
  { id: "copperplate", name: "Copperplate", style: "Copperplate, fantasy", category: "display" },
  { id: "fredoka-one", name: "Fredoka One", style: "'Fredoka One', cursive", category: "display" },
  { id: "fugaz-one", name: "Fugaz One", style: "'Fugaz One', cursive", category: "display" },
  { id: "impact", name: "Impact", style: "Impact, sans-serif", category: "display" },
  { id: "luckiest-guy", name: "Luckiest Guy", style: "'Luckiest Guy', cursive", category: "display" },
  { id: "passion-one", name: "Passion One", style: "'Passion One', cursive", category: "display" },
  { id: "permanent-marker", name: "Permanent Marker", style: "'Permanent Marker', cursive", category: "display" },
  { id: "righteous", name: "Righteous", style: "Righteous, cursive", category: "display" },
  { id: "rockwell", name: "Rockwell", style: "Rockwell, serif", category: "display" },
  { id: "rubik-mono-one", name: "Rubik Mono One", style: "'Rubik Mono One', sans-serif", category: "display" },
  { id: "squada-one", name: "Squada One", style: "'Squada One', cursive", category: "display" },
  { id: "stencil", name: "Stencil", style: "Stencil, fantasy", category: "display" },
  { id: "titan-one", name: "Titan One", style: "'Titan One', cursive", category: "display" },
  { id: "ultra", name: "Ultra", style: "Ultra, serif", category: "display" },
  
  // Monospace (Technical) - Alphabetical
  { id: "consolas", name: "Consolas", style: "Consolas, monospace", category: "mono" },
  { id: "courier", name: "Courier New", style: "'Courier New', monospace", category: "mono" },
  { id: "fira-code", name: "Fira Code", style: "'Fira Code', monospace", category: "mono" },
  { id: "jetbrains-mono", name: "JetBrains Mono", style: "'JetBrains Mono', monospace", category: "mono" },
  { id: "lucida-console", name: "Lucida Console", style: "'Lucida Console', monospace", category: "mono" },
  { id: "source-code", name: "Source Code Pro", style: "'Source Code Pro', monospace", category: "mono" },
  
  // Racing & Automotive - Alphabetical
  { id: "audiowide", name: "Audiowide", style: "Audiowide, cursive", category: "racing" },
  { id: "bungee", name: "Bungee", style: "Bungee, cursive", category: "racing" },
  { id: "chakra-petch", name: "Chakra Petch", style: "'Chakra Petch', sans-serif", category: "racing" },
  { id: "exo", name: "Exo", style: "Exo, sans-serif", category: "racing" },
  { id: "michroma", name: "Michroma", style: "Michroma, sans-serif", category: "racing" },
  { id: "orbitron", name: "Orbitron", style: "Orbitron, sans-serif", category: "racing" },
  { id: "quantico", name: "Quantico", style: "Quantico, sans-serif", category: "racing" },
  { id: "racing-sans", name: "Racing Sans One", style: "'Racing Sans One', cursive", category: "racing" },
  { id: "rajdhani", name: "Rajdhani", style: "Rajdhani, sans-serif", category: "racing" },
  { id: "russo-one", name: "Russo One", style: "'Russo One', sans-serif", category: "racing" },
  { id: "saira-stencil", name: "Saira Stencil", style: "'Saira Stencil One', sans-serif", category: "racing" },
  { id: "teko", name: "Teko", style: "Teko, sans-serif", category: "racing" },
  
  // Sans-Serif (Clean & Modern) - Alphabetical
  { id: "arial", name: "Arial", style: "Arial, sans-serif", category: "sans-serif" },
  { id: "calibri", name: "Calibri", style: "Calibri, sans-serif", category: "sans-serif" },
  { id: "candara", name: "Candara", style: "Candara, sans-serif", category: "sans-serif" },
  { id: "century-gothic", name: "Century Gothic", style: "'Century Gothic', sans-serif", category: "sans-serif" },
  { id: "futura", name: "Futura", style: "Futura, sans-serif", category: "sans-serif" },
  { id: "gill-sans", name: "Gill Sans", style: "'Gill Sans', sans-serif", category: "sans-serif" },
  { id: "helvetica", name: "Helvetica", style: "Helvetica, Arial, sans-serif", category: "sans-serif" },
  { id: "lato", name: "Lato", style: "Lato, sans-serif", category: "sans-serif" },
  { id: "montserrat", name: "Montserrat", style: "Montserrat, sans-serif", category: "sans-serif" },
  { id: "open-sans", name: "Open Sans", style: "'Open Sans', sans-serif", category: "sans-serif" },
  { id: "oswald", name: "Oswald", style: "Oswald, sans-serif", category: "sans-serif" },
  { id: "poppins", name: "Poppins", style: "Poppins, sans-serif", category: "sans-serif" },
  { id: "raleway", name: "Raleway", style: "Raleway, sans-serif", category: "sans-serif" },
  { id: "roboto", name: "Roboto", style: "Roboto, sans-serif", category: "sans-serif" },
  { id: "segoe-ui", name: "Segoe UI", style: "'Segoe UI', sans-serif", category: "sans-serif" },
  { id: "tahoma", name: "Tahoma", style: "Tahoma, sans-serif", category: "sans-serif" },
  { id: "trebuchet", name: "Trebuchet", style: "'Trebuchet MS', sans-serif", category: "sans-serif" },
  { id: "verdana", name: "Verdana", style: "Verdana, sans-serif", category: "sans-serif" },
  
  // Script & Decorative - Alphabetical (Expanded)
  { id: "alex-brush", name: "Alex Brush", style: "'Alex Brush', cursive", category: "script" },
  { id: "allura", name: "Allura", style: "Allura, cursive", category: "script" },
  { id: "amatic-sc", name: "Amatic SC", style: "'Amatic SC', cursive", category: "script" },
  { id: "bad-script", name: "Bad Script", style: "'Bad Script', cursive", category: "script" },
  { id: "brush", name: "Brush Script", style: "'Brush Script MT', cursive", category: "script" },
  { id: "caveat", name: "Caveat", style: "Caveat, cursive", category: "script" },
  { id: "cedarville", name: "Cedarville Cursive", style: "'Cedarville Cursive', cursive", category: "script" },
  { id: "comic", name: "Comic Sans", style: "'Comic Sans MS', cursive", category: "script" },
  { id: "cookie", name: "Cookie", style: "Cookie, cursive", category: "script" },
  { id: "courgette", name: "Courgette", style: "Courgette, cursive", category: "script" },
  { id: "croissant-one", name: "Croissant One", style: "'Croissant One', cursive", category: "script" },
  { id: "dancing-script", name: "Dancing Script", style: "'Dancing Script', cursive", category: "script" },
  { id: "dawning", name: "Dawning of a New Day", style: "'Dawning of a New Day', cursive", category: "script" },
  { id: "gloria-hallelujah", name: "Gloria Hallelujah", style: "'Gloria Hallelujah', cursive", category: "script" },
  { id: "grand-hotel", name: "Grand Hotel", style: "'Grand Hotel', cursive", category: "script" },
  { id: "great-vibes", name: "Great Vibes", style: "'Great Vibes', cursive", category: "script" },
  { id: "handlee", name: "Handlee", style: "Handlee, cursive", category: "script" },
  { id: "homemade-apple", name: "Homemade Apple", style: "'Homemade Apple', cursive", category: "script" },
  { id: "indie-flower", name: "Indie Flower", style: "'Indie Flower', cursive", category: "script" },
  { id: "italianno", name: "Italianno", style: "Italianno, cursive", category: "script" },
  { id: "kalam", name: "Kalam", style: "Kalam, cursive", category: "script" },
  { id: "kaushan-script", name: "Kaushan Script", style: "'Kaushan Script', cursive", category: "script" },
  { id: "la-belle-aurore", name: "La Belle Aurore", style: "'La Belle Aurore', cursive", category: "script" },
  { id: "lobster", name: "Lobster", style: "Lobster, cursive", category: "script" },
  { id: "lobster-two", name: "Lobster Two", style: "'Lobster Two', cursive", category: "script" },
  { id: "lucida-handwriting", name: "Lucida Handwriting", style: "'Lucida Handwriting', cursive", category: "script" },
  { id: "marck-script", name: "Marck Script", style: "'Marck Script', cursive", category: "script" },
  { id: "meow-script", name: "Meow Script", style: "'Meow Script', cursive", category: "script" },
  { id: "mr-dafoe", name: "Mr Dafoe", style: "'Mr Dafoe', cursive", category: "script" },
  { id: "ms-madi", name: "Ms Madi", style: "'Ms Madi', cursive", category: "script" },
  { id: "niconne", name: "Niconne", style: "Niconne, cursive", category: "script" },
  { id: "norican", name: "Norican", style: "Norican, cursive", category: "script" },
  { id: "nothing-you-could-do", name: "Nothing You Could Do", style: "'Nothing You Could Do', cursive", category: "script" },
  { id: "pacifico", name: "Pacifico", style: "Pacifico, cursive", category: "script" },
  { id: "papyrus", name: "Papyrus", style: "Papyrus, fantasy", category: "script" },
  { id: "patrick-hand", name: "Patrick Hand", style: "'Patrick Hand', cursive", category: "script" },
  { id: "pinyon-script", name: "Pinyon Script", style: "'Pinyon Script', cursive", category: "script" },
  { id: "reenie-beanie", name: "Reenie Beanie", style: "'Reenie Beanie', cursive", category: "script" },
  { id: "rochester", name: "Rochester", style: "Rochester, cursive", category: "script" },
  { id: "rouge-script", name: "Rouge Script", style: "'Rouge Script', cursive", category: "script" },
  { id: "sacramento", name: "Sacramento", style: "Sacramento, cursive", category: "script" },
  { id: "satisfy", name: "Satisfy", style: "Satisfy, cursive", category: "script" },
  { id: "segoe-script", name: "Segoe Script", style: "'Segoe Script', cursive", category: "script" },
  { id: "shadows-into-light", name: "Shadows Into Light", style: "'Shadows Into Light', cursive", category: "script" },
  { id: "sniglet", name: "Sniglet", style: "Sniglet, cursive", category: "script" },
  { id: "sofia", name: "Sofia", style: "Sofia, cursive", category: "script" },
  { id: "tangerine", name: "Tangerine", style: "Tangerine, cursive", category: "script" },
  { id: "unkempt", name: "Unkempt", style: "Unkempt, cursive", category: "script" },
  { id: "yellowtail", name: "Yellowtail", style: "Yellowtail, cursive", category: "script" },
  { id: "zeyada", name: "Zeyada", style: "Zeyada, cursive", category: "script" },
  
  // Serif (Classic & Elegant) - Alphabetical
  { id: "baskerville", name: "Baskerville", style: "Baskerville, serif", category: "serif" },
  { id: "bodoni", name: "Bodoni", style: "'Bodoni MT', serif", category: "serif" },
  { id: "book-antiqua", name: "Book Antiqua", style: "'Book Antiqua', Palatino, serif", category: "serif" },
  { id: "cambria", name: "Cambria", style: "Cambria, serif", category: "serif" },
  { id: "constantia", name: "Constantia", style: "Constantia, serif", category: "serif" },
  { id: "didot", name: "Didot", style: "Didot, serif", category: "serif" },
  { id: "garamond", name: "Garamond", style: "Garamond, serif", category: "serif" },
  { id: "georgia", name: "Georgia", style: "Georgia, serif", category: "serif" },
  { id: "merriweather", name: "Merriweather", style: "Merriweather, serif", category: "serif" },
  { id: "palatino", name: "Palatino", style: "'Palatino Linotype', Palatino, serif", category: "serif" },
  { id: "playfair", name: "Playfair Display", style: "'Playfair Display', serif", category: "serif" },
  { id: "times", name: "Times New Roman", style: "'Times New Roman', serif", category: "serif" },
];

// Fit options for inside/outside application
const FIT_OPTIONS = [
  { id: "exterior", name: "Exterior Fit", description: "Standard application on outside surfaces" },
  { id: "interior", name: "Reversed for Interior Glass Fit", description: "Mirrored for applying inside glass" },
];

// Quantity discount tiers
const QUANTITY_DISCOUNTS = [
  { min: 1, max: 3, discount: 0, label: "" },
  { min: 4, max: 6, discount: 0.05, label: "5% off" },
  { min: 7, max: 10, discount: 0.075, label: "7.5% off" },
  { min: 11, max: 19, discount: 0.10, label: "10% off" },
  { min: 20, max: Infinity, discount: 0.20, label: "20% off" },
];

// Get discount tier based on quantity
const getDiscountTier = (qty) => {
  return QUANTITY_DISCOUNTS.find(tier => qty >= tier.min && qty <= tier.max) || QUANTITY_DISCOUNTS[0];
};

export const CustomSticker = () => {
  const { addToCart } = useCart();
  const [adding, setAdding] = useState(false);
  const [downloading, setDownloading] = useState(false);
  
  // Form state
  const [customText, setCustomText] = useState("");
  const [selectedFont, setSelectedFont] = useState(FONT_OPTIONS[0]);
  const [selectedHexisColor, setSelectedHexisColor] = useState(null);
  const [showHexisPalette, setShowHexisPalette] = useState(false);
  const [selectedSize, setSelectedSize] = useState(SIZE_OPTIONS[0]); // Default to 100mm (£10.99)
  const [selectedFit, setSelectedFit] = useState(FIT_OPTIONS[0]); // Default to Exterior Fit
  const [quantity, setQuantity] = useState(1);

  // TinyMCE editor configuration with Social Icons
  const tinymceInit = useMemo(() => ({
    height: 150,
    menubar: false,
    plugins: ['autolink', 'lists', 'link', 'charmap'],
    toolbar: 'bold italic | socialicons | removeformat',
    // Allow SVG elements for social icons
    extended_valid_elements: 'svg[xmlns|viewBox|fill|class|width|height|style],path[d|fill|class],g[*],circle[*],rect[*],span[class|style]',
    valid_children: '+body[svg|span],+p[svg|span],+span[svg]',
    content_style: `
      body { 
        font-family: ${selectedFont.style}; 
        font-size: 18px; 
        text-align: center;
        padding: 10px;
      } 
      svg, .social-icon { 
        display: inline-block !important; 
        width: 1em !important; 
        height: 1em !important; 
        vertical-align: middle !important; 
        margin: 0 0.1em !important; 
      }
      .social-icon-wrapper {
        display: inline !important;
      }
      p { margin: 0; }
    `,
    branding: false,
    promotion: false,
    license_key: 'gpl',
    statusbar: false,
    setup: (editor) => {
      // Social Media Icons - Single Color SVGs
      const socialIcons = [
        { name: 'Facebook', icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="social-icon"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>' },
        { name: 'Instagram', icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="social-icon"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>' },
        { name: 'Twitter/X', icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="social-icon"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>' },
        { name: 'YouTube', icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="social-icon"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>' },
        { name: 'TikTok', icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="social-icon"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>' },
        { name: 'LinkedIn', icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="social-icon"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>' },
        { name: 'Pinterest', icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="social-icon"><path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.758-1.379l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.55.535 6.607 0 11.985-5.365 11.985-11.987C23.97 5.39 18.592.026 11.985.026L12.017 0z"/></svg>' },
        { name: 'WhatsApp', icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="social-icon"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>' },
        { name: 'Snapchat', icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="social-icon"><path d="M12.206.793c.99 0 4.347.276 5.93 3.821.529 1.193.403 3.219.299 4.847l-.003.06c-.012.18-.022.345-.03.51.075.045.203.09.401.09.3-.016.659-.12 1.033-.301.165-.088.344-.104.464-.104.182 0 .359.029.509.09.45.149.734.479.734.838.015.449-.39.839-1.213 1.168-.089.029-.209.075-.344.119-.45.135-1.139.36-1.333.81-.09.224-.061.524.12.868l.015.015c.06.136 1.526 3.475 4.791 4.014.255.044.435.27.42.509 0 .075-.015.149-.045.225-.24.569-1.273.988-3.146 1.271-.059.091-.12.375-.164.57-.029.179-.074.36-.134.553-.076.271-.27.405-.555.405h-.03c-.135 0-.313-.031-.538-.074-.36-.075-.765-.135-1.273-.135-.3 0-.599.015-.913.074-.6.104-1.123.464-1.723.884-.853.599-1.826 1.288-3.294 1.288-.06 0-.119-.015-.18-.015h-.149c-1.468 0-2.427-.675-3.279-1.288-.599-.42-1.107-.779-1.707-.884-.314-.045-.629-.074-.928-.074-.54 0-.958.089-1.272.149-.211.043-.391.074-.54.074-.374 0-.523-.224-.583-.42-.061-.192-.09-.389-.135-.567-.046-.181-.105-.494-.166-.57-1.918-.222-2.95-.642-3.189-1.226-.031-.063-.052-.15-.055-.225-.015-.243.165-.465.42-.509 3.264-.54 4.73-3.879 4.791-4.02l.016-.029c.18-.345.224-.645.119-.869-.195-.434-.884-.658-1.332-.809-.121-.029-.24-.074-.346-.119-1.107-.435-1.257-.93-1.197-1.273.09-.479.674-.793 1.168-.793.146 0 .27.029.383.074.42.194.789.3 1.104.3.234 0 .384-.06.465-.105l-.046-.569c-.098-1.626-.225-3.651.307-4.837C7.392 1.077 10.739.807 11.727.807l.419-.015h.06z"/></svg>' },
        { name: 'Telegram', icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="social-icon"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>' },
        { name: 'Email', icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="social-icon"><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>' },
        { name: 'Website', icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="social-icon"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/></svg>' },
        { name: 'Phone', icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="social-icon"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/></svg>' },
      ];
      
      editor.ui.registry.addMenuButton('socialicons', {
        text: 'Social Icons',
        tooltip: 'Insert Social Media Icon',
        fetch: (callback) => {
          const items = socialIcons.map(social => ({
            type: 'menuitem',
            text: social.name,
            onAction: () => {
              // Insert icon with a unique marker, then position cursor after it
              const uniqueId = 'social-icon-' + Date.now();
              editor.insertContent(`<span id="${uniqueId}" class="social-icon-wrapper" style="display:inline;">${social.icon}</span>&nbsp;`);
              
              // Find the inserted element and move cursor after it
              const insertedNode = editor.dom.get(uniqueId);
              if (insertedNode) {
                // Remove the temporary ID
                insertedNode.removeAttribute('id');
                // Move cursor after the inserted node
                editor.selection.select(insertedNode);
                editor.selection.collapse(false);
              }
            }
          }));
          callback(items);
        }
      });
    }
  }), [selectedFont.style]);

  // Calculate number of lines (minimum 1) - parse HTML to count lines
  const lineCount = useMemo(() => {
    if (!customText.trim()) return 1;
    // Strip HTML tags for line counting
    const textOnly = customText.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ');
    if (!textOnly.trim()) return 1;
    // Count paragraph breaks from HTML
    const paragraphs = customText.split(/<\/p>|<br\s*\/?>/i).filter(line => {
      const stripped = line.replace(/<[^>]*>/g, '').trim();
      return stripped !== '';
    });
    return Math.max(1, paragraphs.length);
  }, [customText]);

  // Get plain text for character count
  const plainTextLength = useMemo(() => {
    return customText.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').length;
  }, [customText]);

  // Calculate total price with quantity discounts and premium vinyl markup
  const isSkintacColor = selectedHexisColor?.series === "HX20000";
  const isSuptacHXColor = selectedHexisColor?.series === "HXS5000";
  const vinylMultiplier = isSkintacColor ? 1.5 : (isSuptacHXColor ? 1.4 : 1);
  
  const basePricePerLine = selectedSize.price;
  const pricePerLine = basePricePerLine * vinylMultiplier;
  const discountTier = useMemo(() => getDiscountTier(quantity), [quantity]);
  const subtotalPrice = pricePerLine * lineCount * quantity;
  const discountAmount = subtotalPrice * discountTier.discount;
  const totalPrice = (subtotalPrice - discountAmount).toFixed(2);

  // Reset form
  const handleReset = () => {
    setCustomText("");
    setSelectedFont(FONT_OPTIONS[0]);
    setSelectedHexisColor(null);
    setShowHexisPalette(false);
    setSelectedSize(SIZE_OPTIONS[0]);
    setSelectedFit(FIT_OPTIONS[0]);
    setQuantity(1);
  };

  // Download PDF preview
  const handleDownloadPDF = async () => {
    // Strip HTML to check if there's actual text content
    const plainText = customText.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    if (!plainText) {
      toast.error("Please enter your custom text first");
      return;
    }

    setDownloading(true);
    
    try {
      const colorName = selectedHexisColor ? selectedHexisColor.name : "Black";
      const colorHex = selectedHexisColor ? selectedHexisColor.hex : "#000000";
      
      const response = await fetch(`${API}/custom-sticker/preview-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: customText,
          font: selectedFont.name,
          color: colorName,
          color_hex: colorHex,
          size: selectedSize.name,
          dimensions: selectedSize.dimensions,
          price: selectedSize.price,
          fit: selectedFit.name
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate PDF');
      }

      // Get the blob and download it
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'Ministripes_Custom_Preview.pdf';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF Preview Downloaded!", {
        description: "Check your downloads folder"
      });
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error("Failed to download preview");
    } finally {
      setDownloading(false);
    }
  };

  // Add custom sticker to cart
  const handleAddToCart = async () => {
    // Strip HTML to check if there's actual text content
    const plainText = customText.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    if (!plainText) {
      toast.error("Please enter your custom text");
      return;
    }

    setAdding(true);
    
    const colorName = selectedHexisColor ? selectedHexisColor.name : "Black";
    const colorCode = selectedHexisColor ? selectedHexisColor.code : "";
    
    // Create detailed instructions for the custom sticker (includes HTML for social icons)
    const instructions = `CUSTOM TEXT DECAL ORDER
Text: "${customText}"
Lines: ${lineCount}
Font: ${selectedFont.name}
Color: ${colorName}${colorCode ? ` (${colorCode})` : ''}
Size: ${selectedSize.name} - (${selectedSize.dimensions})
Fit: ${selectedFit.name}
Price per line: £${pricePerLine.toFixed(2)}`;

    // We'll use a special product ID for custom stickers
    // For now, we'll add it as a cart item with instructions
    const result = await addToCart(
      "custom-text-sticker", // Special product ID
      quantity,
      colorName,
      instructions
    );

    if (result.success) {
      toast.success("Custom decal added to cart!", {
        description: `${quantity}x ${lineCount} line${lineCount > 1 ? 's' : ''} - ${selectedSize.name} - £${totalPrice}`
      });
    } else {
      toast.error("Failed to add to cart. Please try again.");
    }
    
    setAdding(false);
  };

  return (
    <div className="pt-24 pb-16" data-testid="custom-sticker-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 
            className="text-3xl md:text-4xl lg:text-5xl font-normal tracking-tight mb-4"
            style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
          >
            Custom Text Decals
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-6">
            Single or Multiple Lines
          </p>
          <div className="text-sm text-muted-foreground max-w-2xl mx-auto space-y-1">
            <p>Two decals featuring any text you require</p>
            <p>Multiple lines available - each additional line doubles the price</p>
            <p>Precision-cut die-cut vinyl with no background</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Preview Section */}
          <div className="order-2 lg:order-1">
            <div className="sticky top-28">
              <h2 className="text-sm font-medium uppercase tracking-wider mb-4 flex items-center gap-2">
                <Eye className="h-4 w-4" />
                Live Preview
              </h2>
              
              {/* Preview Card with Inline Text Input */}
              <div 
                className="bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg overflow-hidden"
                data-testid="sticker-preview"
              >
                {/* Text Input Inside Preview - TinyMCE Editor with Social Icons */}
                <div className="p-4 border-b border-gray-200 bg-white/50">
                  <div data-testid="preview-text-input" className="bg-white border border-gray-300 rounded-lg overflow-hidden">
                    <Editor
                      tinymceScriptSrc="/tinymce/tinymce.min.js"
                      value={customText}
                      onEditorChange={(content) => setCustomText(content)}
                      init={tinymceInit}
                    />
                  </div>
                </div>
                
                {/* Live Preview Display */}
                <div className="aspect-video flex items-center justify-center p-8">
                  <div className="text-center w-full">
                    <div
                      className="preview-content"
                      style={{
                        fontFamily: selectedFont.style,
                        color: selectedHexisColor ? selectedHexisColor.hex : "#000000",
                        fontSize: lineCount > 2 ? '1.5rem' : lineCount > 1 ? '2rem' : '3rem',
                        textShadow: selectedHexisColor?.hex === "#FFFFFF" || selectedHexisColor?.hex === "#F5F5F5" ? '1px 1px 2px rgba(0,0,0,0.3)' : 'none',
                        wordBreak: 'break-word',
                        lineHeight: 1.6,
                        textAlign: 'center',
                      }}
                      data-testid="preview-text"
                      dangerouslySetInnerHTML={{ __html: customText || "Your Text Here" }}
                    />
                    <style>{`
                      .preview-content svg,
                      .preview-content .social-icon {
                        display: inline !important;
                        width: 1em !important;
                        height: 1em !important;
                        vertical-align: middle !important;
                        margin: 0 0.1em !important;
                      }
                      .preview-content .social-icon-wrapper {
                        display: inline !important;
                      }
                      .preview-content p {
                        margin: 0 !important;
                        padding: 0 !important;
                        display: inline !important;
                      }
                    `}</style>
                  </div>
                </div>
              </div>

              {/* Preview Info */}
              <div className="mt-4 p-4 bg-secondary/50 rounded-lg">
                <h3 className="text-sm font-medium mb-2">Preview Information</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li><strong>Lines:</strong> {lineCount} {lineCount > 1 && `(price × ${lineCount})`}</li>
                  <li><strong>Size:</strong> {selectedSize.name} - ({selectedSize.dimensions})</li>
                  <li><strong>Font:</strong> {selectedFont.name}</li>
                  <li><strong>Colour:</strong> {selectedHexisColor ? `${selectedHexisColor.name} (${selectedHexisColor.code})` : 'Not selected'}</li>
                  <li><strong>Fit:</strong> {selectedFit.name}</li>
                  <li><strong>Material:</strong> Hexis Suptac Premium Vinyl (10-year exterior grade)</li>
                  <li><strong>Quantity:</strong> 2 decals per order</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Customization Form */}
          <div className="order-1 lg:order-2 space-y-8">
            {/* Text Input Note - since input is now in preview */}
            <div className="p-4 bg-secondary/30 rounded-lg">
              <p className="text-sm text-muted-foreground">
                <strong>Enter your text</strong> in the editor on the left. Use the <strong>Social Icons</strong> button to add social media icons!
              </p>
              <div className="flex justify-between text-xs text-muted-foreground mt-2">
                <span>{lineCount} line{lineCount > 1 ? 's' : ''} {lineCount > 1 && `(${lineCount}x price)`}</span>
                <span>{plainTextLength}/150 characters</span>
              </div>
            </div>

            {/* Font Selection - Dropdown */}
            <div className="space-y-3">
              <label className="text-sm font-medium uppercase tracking-wider">
                Select Font Style
              </label>
              <Select
                value={selectedFont.id}
                onValueChange={(value) => {
                  const font = FONT_OPTIONS.find(f => f.id === value);
                  if (font) setSelectedFont(font);
                }}
              >
                <SelectTrigger className="w-full h-14 text-lg rounded-full border-0 bg-black text-white" data-testid="font-dropdown">
                  <SelectValue placeholder="Select a font">
                    <span className="text-white" style={{ fontFamily: selectedFont.style }}>{selectedFont.name}</span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="max-h-80 bg-white border-0">
                  {/* Sans-Serif */}
                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-muted/50">
                    Sans-Serif (Clean & Modern)
                  </div>
                  {FONT_OPTIONS.filter(f => f.category === 'sans-serif').map((font) => (
                    <SelectItem 
                      key={font.id} 
                      value={font.id}
                      className="py-3 cursor-pointer !bg-white !text-black hover:!bg-black hover:!text-white focus:!bg-black focus:!text-white data-[state=checked]:!bg-black data-[state=checked]:!text-white"
                      data-testid={`font-${font.id}`}
                    >
                      <span style={{ fontFamily: font.style }} className="text-base">{font.name}</span>
                    </SelectItem>
                  ))}
                  
                  {/* Serif */}
                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-muted/50 mt-1">
                    Serif (Classic & Elegant)
                  </div>
                  {FONT_OPTIONS.filter(f => f.category === 'serif').map((font) => (
                    <SelectItem 
                      key={font.id} 
                      value={font.id}
                      className="py-3 cursor-pointer !bg-white !text-black hover:!bg-black hover:!text-white focus:!bg-black focus:!text-white data-[state=checked]:!bg-black data-[state=checked]:!text-white"
                      data-testid={`font-${font.id}`}
                    >
                      <span style={{ fontFamily: font.style }} className="text-base">{font.name}</span>
                    </SelectItem>
                  ))}
                  
                  {/* Display */}
                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-muted/50 mt-1">
                    Display (High Impact)
                  </div>
                  {FONT_OPTIONS.filter(f => f.category === 'display').map((font) => (
                    <SelectItem 
                      key={font.id} 
                      value={font.id}
                      className="py-3 cursor-pointer !bg-white !text-black hover:!bg-black hover:!text-white focus:!bg-black focus:!text-white data-[state=checked]:!bg-black data-[state=checked]:!text-white"
                      data-testid={`font-${font.id}`}
                    >
                      <span style={{ fontFamily: font.style }} className="text-base">{font.name}</span>
                    </SelectItem>
                  ))}
                  
                  {/* Script & Decorative */}
                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-muted/50 mt-1">
                    Script & Decorative
                  </div>
                  {FONT_OPTIONS.filter(f => f.category === 'script').map((font) => (
                    <SelectItem 
                      key={font.id} 
                      value={font.id}
                      className="py-3 cursor-pointer !bg-white !text-black hover:!bg-black hover:!text-white focus:!bg-black focus:!text-white data-[state=checked]:!bg-black data-[state=checked]:!text-white"
                      data-testid={`font-${font.id}`}
                    >
                      <span style={{ fontFamily: font.style }} className="text-base">{font.name}</span>
                    </SelectItem>
                  ))}
                  
                  {/* Monospace */}
                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-muted/50 mt-1">
                    Monospace (Technical)
                  </div>
                  {FONT_OPTIONS.filter(f => f.category === 'mono').map((font) => (
                    <SelectItem 
                      key={font.id} 
                      value={font.id}
                      className="py-3 cursor-pointer !bg-white !text-black hover:!bg-black hover:!text-white focus:!bg-black focus:!text-white data-[state=checked]:!bg-black data-[state=checked]:!text-white"
                      data-testid={`font-${font.id}`}
                    >
                      <span style={{ fontFamily: font.style }} className="text-base">{font.name}</span>
                    </SelectItem>
                  ))}
                  
                  {/* Racing & Automotive */}
                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-muted/50 mt-1">
                    Racing & Automotive
                  </div>
                  {FONT_OPTIONS.filter(f => f.category === 'racing').map((font) => (
                    <SelectItem 
                      key={font.id} 
                      value={font.id}
                      className="py-3 cursor-pointer !bg-white !text-black hover:!bg-black hover:!text-white focus:!bg-black focus:!text-white data-[state=checked]:!bg-black data-[state=checked]:!text-white"
                      data-testid={`font-${font.id}`}
                    >
                      <span style={{ fontFamily: font.style }} className="text-base">{font.name}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {FONT_OPTIONS.length} fonts available across 6 categories
              </p>
            </div>

            {/* Size Selection - Dropdown */}
            <div className="space-y-3">
              <label className="text-sm font-medium uppercase tracking-wider">
                Select Size (Length)
              </label>
              <Select
                value={selectedSize.id}
                onValueChange={(value) => {
                  const size = SIZE_OPTIONS.find(s => s.id === value);
                  if (size) setSelectedSize(size);
                }}
              >
                <SelectTrigger className="w-full h-14 text-lg rounded-full border-0 bg-black text-white" data-testid="size-dropdown">
                  <SelectValue placeholder="Select a size">
                    <div className="flex justify-between items-center w-full pr-4">
                      <span className="font-medium text-white">{selectedSize.name}</span>
                      <span className="text-white/70 ml-4">£{selectedSize.price.toFixed(2)}</span>
                    </div>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="max-h-80 bg-white border-0">
                  {SIZE_OPTIONS.map((size) => (
                    <SelectItem 
                      key={size.id} 
                      value={size.id}
                      className="py-3 cursor-pointer !bg-white !text-black hover:!bg-black hover:!text-white focus:!bg-black focus:!text-white data-[state=checked]:!bg-black data-[state=checked]:!text-white"
                      data-testid={`size-${size.id}`}
                    >
                      <div className="flex justify-between items-center w-full gap-8">
                        <div>
                          <span className="font-medium">{size.name}</span>
                          <span className="text-sm opacity-70 ml-2">({size.dimensions})</span>
                        </div>
                        <span className="font-bold text-right">£{size.price.toFixed(2)}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Sizes range from 100mm (£10.99) to 3556mm (£79.99)
              </p>
            </div>

            {/* Fit Selection - Inside/Outside */}
            <div className="space-y-3">
              <label className="text-sm font-medium uppercase tracking-wider">
                Inside / Outside Fit
              </label>
              <Select
                value={selectedFit.id}
                onValueChange={(value) => {
                  const fit = FIT_OPTIONS.find(f => f.id === value);
                  if (fit) setSelectedFit(fit);
                }}
              >
                <SelectTrigger 
                  className="w-full h-14 text-lg rounded-full border-0 bg-black text-white" 
                  data-testid="fit-dropdown"
                >
                  <SelectValue placeholder="Select fit type">
                    <span className="font-medium text-white">{selectedFit.name}</span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="bg-white border-0">
                  {FIT_OPTIONS.map((fit) => (
                    <SelectItem 
                      key={fit.id} 
                      value={fit.id}
                      className="py-3 cursor-pointer !bg-white !text-black hover:!bg-black hover:!text-white focus:!bg-black focus:!text-white data-[state=checked]:!bg-black data-[state=checked]:!text-white"
                      data-testid={`fit-${fit.id}`}
                    >
                      <div className="flex flex-col">
                        <span className="font-medium">{fit.name}</span>
                        <span className="text-xs opacity-70">{fit.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Choose exterior for outside surfaces or interior for applying inside glass windows
              </p>
            </div>

            {/* Color Selection - HEXIS Palette */}
            <div className="space-y-3">
              <label className="text-sm font-medium uppercase tracking-wider">
                Select Colour: <span className="font-normal">{selectedHexisColor ? selectedHexisColor.name : 'Not selected'}</span>
              </label>
              
              {/* HEXIS Color Palette Toggle - Rainbow Pill Tab */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setShowHexisPalette(!showHexisPalette)}
                  className={`flex items-center gap-2 px-5 py-2.5 text-sm font-semibold transition-all rounded-full border-2 border-black ${
                    showHexisPalette || selectedHexisColor
                      ? 'text-white'
                      : 'text-black hover:scale-105'
                  }`}
                  style={{
                    background: showHexisPalette || selectedHexisColor
                      ? '#000000'
                      : 'linear-gradient(90deg, #ff0000 0%, #ff8000 14%, #ffff00 28%, #00ff00 42%, #00ffff 57%, #0080ff 71%, #8000ff 85%, #ff0080 100%)',
                    boxShadow: showHexisPalette || selectedHexisColor
                      ? 'none'
                      : '0 2px 8px rgba(0,0,0,0.15)',
                  }}
                  data-testid="hexis-palette-toggle"
                >
                  <Palette className="h-4 w-4" />
                  {showHexisPalette ? 'Hide HEXIS Palette' : 'Browse 216 HEXIS Vinyl Colours'}
                </button>
              </div>
              
              {/* Selected HEXIS Color Display */}
              {selectedHexisColor && !showHexisPalette && (
                <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg border-2 border-green-200">
                  <div className="w-12 h-12 rounded-lg border border-gray-300 flex-shrink-0 relative overflow-hidden shadow-md">
                    <div 
                      className="absolute inset-0"
                      style={{ backgroundColor: selectedHexisColor.hex }}
                    />
                    <div 
                      className="absolute inset-0"
                      style={{
                        background: `linear-gradient(145deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.15) 25%, rgba(255,255,255,0) 50%, rgba(0,0,0,0.05) 75%, rgba(0,0,0,0.15) 100%)`,
                      }}
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-green-700 font-medium uppercase tracking-wider">Selected Colour</p>
                    <p className="font-bold text-base truncate">{selectedHexisColor.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {selectedHexisColor.code}
                      {selectedHexisColor.series && ` • ${selectedHexisColor.series}`}
                      {selectedHexisColor.finish && ` • ${selectedHexisColor.finish}`}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedHexisColor(null)}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                  >
                    Change
                  </button>
                </div>
              )}
              
              {/* HEXIS Color Palette Panel */}
              {showHexisPalette && (
                <div className="mt-3 p-4 border-2 border-black rounded-lg bg-white">
                  <HexisColorPalette
                    onSelectColor={(color) => {
                      setSelectedHexisColor(color);
                      setShowHexisPalette(false);
                      toast.success(`Selected: ${color.name}`, {
                        description: `${color.code} - ${color.series || 'HEXIS'} ${color.finish || ''}`
                      });
                    }}
                    selectedColor={selectedHexisColor}
                    showAllSeries={true}
                  />
                </div>
              )}
            </div>

            {/* Quantity */}
            <div className="space-y-3">
              <label className="text-sm font-medium uppercase tracking-wider">
                Quantity
              </label>
              <div className="flex items-center gap-4">
                <div className="flex items-center border border-input">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="p-3 hover:bg-secondary transition-colors"
                    data-testid="decrease-qty"
                  >
                    -
                  </button>
                  <span className="w-12 text-center font-medium">{quantity}</span>
                  <button
                    onClick={() => setQuantity(Math.min(99, quantity + 1))}
                    className="p-3 hover:bg-secondary transition-colors"
                    data-testid="increase-qty"
                  >
                    +
                  </button>
                </div>
                {discountTier.discount > 0 && (
                  <span className="text-green-600 font-semibold text-sm bg-green-50 px-3 py-1 rounded-full" data-testid="discount-badge">
                    {discountTier.label}
                  </span>
                )}
              </div>
              
              {/* Quantity Discount Tiers */}
              <div className="bg-secondary/50 p-3 rounded-lg">
                <p className="text-xs font-medium text-muted-foreground mb-2">BULK DISCOUNTS:</p>
                <div className="grid grid-cols-2 gap-1 text-xs">
                  {QUANTITY_DISCOUNTS.filter(tier => tier.discount > 0).map((tier, idx) => (
                    <div 
                      key={idx}
                      className={`flex justify-between px-2 py-1 rounded ${
                        quantity >= tier.min && quantity <= tier.max 
                          ? 'bg-green-100 text-green-700 font-semibold' 
                          : 'text-muted-foreground'
                      }`}
                    >
                      <span>{tier.max === Infinity ? `${tier.min}+` : `${tier.min}-${tier.max}`}</span>
                      <span>{tier.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Price Summary */}
            <div className="border-t border-b border-border py-6 space-y-2">
              <div className="flex justify-between text-sm">
                <span>Base price per line:</span>
                <span>£{basePricePerLine.toFixed(2)}</span>
              </div>
              {(isSkintacColor || isSuptacHXColor) && (
                <div className="flex justify-between text-sm text-amber-600 font-medium">
                  <span>Premium vinyl ({isSkintacColor ? '+50%' : '+40%'}):</span>
                  <span>+£{((pricePerLine - basePricePerLine)).toFixed(2)}</span>
                </div>
              )}
              {(isSkintacColor || isSuptacHXColor) && (
                <div className="flex justify-between text-sm font-medium">
                  <span>Price per line (with premium):</span>
                  <span>£{pricePerLine.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span>Number of lines:</span>
                <span>x{lineCount}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Quantity:</span>
                <span>x{quantity}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Subtotal:</span>
                <span>£{subtotalPrice.toFixed(2)}</span>
              </div>
              {discountTier.discount > 0 && (
                <div className="flex justify-between text-sm text-green-600 font-medium" data-testid="discount-line">
                  <span>Bulk Discount ({discountTier.label}):</span>
                  <span>-£{discountAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-xl font-bold pt-2 border-t border-border mt-2">
                <span>Total:</span>
                <span className="price-font" data-testid="total-price">£{totalPrice}</span>
              </div>
              {(isSkintacColor || isSuptacHXColor) && (
                <p className="text-xs text-amber-600 text-right">
                  {isSkintacColor ? 'Premium Skintac vinyl selected' : 'Premium Suptac HX Air release vinyl selected'}
                </p>
              )}
              {discountTier.discount > 0 && (
                <p className="text-xs text-green-600 text-right" data-testid="savings-text">
                  You save £{discountAmount.toFixed(2)}!
                </p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-3">
              <div className="flex gap-4">
                <Button
                  variant="outline"
                  size="lg"
                  className="rounded-full h-14"
                  onClick={handleReset}
                  data-testid="reset-btn"
                >
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Reset
                </Button>
                <Button
                  size="lg"
                  className="flex-1 rounded-full h-14 uppercase tracking-widest font-bold"
                  onClick={handleAddToCart}
                  disabled={adding || plainTextLength === 0}
                  data-testid="add-to-cart-btn"
                >
                  {adding ? (
                    "Adding..."
                  ) : (
                    <>
                      <ShoppingCart className="mr-2 h-5 w-5" />
                      Add to Cart - £{totalPrice}
                    </>
                  )}
              </Button>
              </div>
              
              {/* Download PDF Preview Button */}
              <Button
                variant="outline"
                size="lg"
                className="w-full rounded-full h-12 border-2 border-black hover:bg-black hover:text-white transition-colors"
                onClick={handleDownloadPDF}
                disabled={downloading || plainTextLength === 0}
                data-testid="download-pdf-btn"
              >
                {downloading ? (
                  "Generating PDF..."
                ) : (
                  <>
                    <Download className="mr-2 h-5 w-5" />
                    Download Preview PDF
                  </>
                )}
              </Button>
            </div>

            {/* Product Details */}
            <div className="bg-secondary/30 p-4 rounded-lg space-y-3 text-sm">
              <h4 className="font-medium">Product Details:</h4>
              <ul className="list-disc list-inside text-muted-foreground space-y-1">
                <li>Two decals featuring any text you require (single-line layout)</li>
                <li>Precision-cut die-cut vinyl with no background</li>
                <li>Produced using <strong>Hexis Suptac premium 10-year exterior-grade vinyl</strong></li>
                <li>Supplied with quality paper application tape for easy, clean installation</li>
              </ul>
              
              <h4 className="font-medium pt-2">Production & Delivery:</h4>
              <ul className="list-disc list-inside text-muted-foreground space-y-1">
                <li>All custom decals are made to order</li>
                <li>Production time: 2-3 working days</li>
                <li>Suitable for cars, vans, windows, and any smooth surface</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomSticker;
